import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, TouchableOpacity } from 'react-native';
import styled from 'styled-components/native';

import { PORTAL_GATE_ALERT } from '@webtrader/core/constants/portal';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { rgba } from '@webtrader/core/themes/utils';
import { isDarkTheme } from '@webtrader/core/validators/device';

import { removeGate } from '../Portal/PortalUtil';

export interface IWebModalProps {
  message: string;
  title?: string;
  actionText?: string;
  noButton?: boolean;
  onClose?: () => void;
  isVisible?: boolean;
  usePortal?: boolean;
}

const IOS_MODAL_COLORS = {
  background: {
    dark: 'rgb(39, 39, 42)',
    light: 'rgb(236, 236, 236)'
  },
  border: {
    dark: 'rgb(56, 55, 59)',
    light: 'rgb(223, 224, 224)'
  },
  text: {
    dark: 'rgb(72, 136, 234)',
    light: 'rgb(37, 128, 248)'
  }
};

const WebModal: FC<IWebModalProps> = (props) => {
  const { message, title = '', actionText, noButton, onClose, isVisible, usePortal = true } = props;
  const [internalVisible, setInternalVisible] = useState<boolean>(true);

  const visible = usePortal ? internalVisible : isVisible;

  const onCloseClick = useCallback(() => {
    if (noButton) {
      return;
    }
    if (usePortal) {
      setInternalVisible(false);
    }

    removeGate(PORTAL_GATE_ALERT);
    onClose?.();
  }, [noButton, usePortal, onClose]);

  useEffect(() => {
    return () => {
      setInternalVisible(false);
    };
  }, []);

  const { theme } = useTheme();

  const { modalBorderColor, modalBackgroundColor, submitTextColor } = useMemo(() => {
    const { scheme } = theme;
    const darkThemeEnabled = isDarkTheme(scheme);

    const backgroundColor = darkThemeEnabled ? IOS_MODAL_COLORS.background.dark : IOS_MODAL_COLORS.background.light;
    const borderColor = darkThemeEnabled ? IOS_MODAL_COLORS.border.dark : IOS_MODAL_COLORS.border.light;
    const textColor = darkThemeEnabled ? IOS_MODAL_COLORS.text.dark : IOS_MODAL_COLORS.text.light;

    return {
      modalBorderColor: borderColor,
      modalBackgroundColor: backgroundColor,
      submitTextColor: textColor
    };
  }, [theme]);

  return (
    <Modal visible={visible} transparent={true} animationType="fade">
      <StyledOverlay>
        <StyledContainer backgroundColor={modalBackgroundColor} testID={EDataTestId.NOTIFICATION_BOX}>
          <StyledModalContent>
            {title.length > 0 && (
              <StyledModalTitle color={theme.colors.text[0]} testID={EDataTestId.NOTIFICATION_BOX_TITLE}>
                {title}
              </StyledModalTitle>
            )}
            <StyledModalMessage color={theme.colors.text[0]} testID={EDataTestId.NOTIFICATION_BOX_DESCRIPTION}>
              {message}
            </StyledModalMessage>
          </StyledModalContent>
          {!noButton && (
            <StyledModalAction borderColor={modalBorderColor}>
              <StyledModalActionButton onPress={onCloseClick} testID={EDataTestId.NOTIFICATION_BOX_CLOSE}>
                <StyledModalActionText color={submitTextColor}>{actionText}</StyledModalActionText>
              </StyledModalActionButton>
            </StyledModalAction>
          )}
        </StyledContainer>
      </StyledOverlay>
    </Modal>
  );
};

export default WebModal;

const StyledOverlay = styled.View`
  flex: 1;

  align-items: center;
  justify-content: center;

  background-color: ${rgba('rgb(0, 0, 0)', 0.4)};
`;

const StyledContainer = styled.View<{ backgroundColor: string }>`
  min-height: ${dynamicSize.convertSize(80)}px;
  width: ${dynamicSize.convertSize(85)}%;

  border-radius: ${dynamicSize.convertSize(12)}px;

  align-items: center;
  justify-content: center;

  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const StyledModalContent = styled.View`
  width: ${dynamicSize.convertSize(90)}%;

  align-items: center;
  justify-content: center;
  text-align: center;

  border-radius: ${dynamicSize.convertSize(12)}px;

  padding: ${dynamicSize.convertSize(16)}px ${dynamicSize.convertSize(8)}px;
`;

const StyledBaseText = styled.Text<{ color: string }>`
  color: ${({ color }) => color};

  font-family: ${({ theme }) => theme.font.DEFAULT_FONT_FAMILY};

  font-size: ${dynamicSize.convertSize(13)}px;
`;

const StyledModalTitle = styled(StyledBaseText)`
  font-size: ${dynamicSize.convertSize(14)}px;
  font-weight: 500;

  padding-bottom: ${dynamicSize.convertSize(4)}px;
`;

const StyledModalMessage = styled(StyledBaseText)`
  opacity: 0.8;
  text-align: center;
`;

const StyledModalAction = styled.View<{ borderColor: string }>`
  width: 100%;

  align-items: center;
  justify-content: center;

  border-top-width: 1px;
  border-top-color: ${({ borderColor }) => borderColor};

  padding: ${dynamicSize.convertSize(10)}px 0;
`;

const StyledModalActionButton = styled(TouchableOpacity)`
  width: 100%;

  align-items: center;

  font-size: ${dynamicSize.convertSize(12)}px;
`;

const StyledModalActionText = styled(StyledBaseText)`
  width: 100%;

  color: ${({ color }) => color};

  font-size: ${dynamicSize.convertSize(14)}px;

  text-align: center;
`;
